import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  logout,
  fetchUserProfile,
  updateUserProfile,
  saveUserAddress,
  deleteUserAddress,
  deleteAccount,
} from '../../redux/slices/authSlice';
import { clearCart } from '../../redux/slices/cartSlice';
import MyOrdersPage from './MyOrdersPage';

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

const INDIA_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh',
  'Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka',
  'Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram',
  'Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
  'Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
  'Andaman and Nicobar Islands','Chandigarh','Dadra and Nagar Haveli and Daman and Diu',
  'Delhi','Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry',
];

const EMPTY_ADDR = {
  label: 'Home', firstName: '', lastName: '',
  address: '', city: '', state: '', postalCode: '', country: 'India', phone: '',
};

// ─────────────────────────────────────────────────────────────
// Profile Page
// ─────────────────────────────────────────────────────────────

const Profile = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { user, profileLoading } = useSelector((s) => s.auth);

  // ── tabs ──────────────────────────────────────────────────
  const [tab, setTab] = useState('orders'); // 'orders' | 'profile' | 'addresses'

  // ── phone edit ────────────────────────────────────────────
  const [editingPhone, setEditingPhone]   = useState(false);
  const [phoneVal,     setPhoneVal]       = useState('');
  const [phoneSaving,  setPhoneSaving]    = useState(false);
  const [phoneError,   setPhoneError]     = useState('');
  const [phoneSuccess, setPhoneSuccess]   = useState(false);

  // ── address form ──────────────────────────────────────────
  const [showAddrForm, setShowAddrForm]   = useState(false);
  const [addrForm,     setAddrForm]       = useState(EMPTY_ADDR);
  const [addrSaving,   setAddrSaving]     = useState(false);
  const [addrError,    setAddrError]      = useState('');
  const [deletingId,   setDeletingId]     = useState(null);

  // ── delete account modal ──────────────────────────────────
  const [showDeleteModal,    setShowDeleteModal]    = useState(false);
  const [deleteConfirmText,  setDeleteConfirmText]  = useState('');
  const [deleteLoading,      setDeleteLoading]      = useState(false);
  const [deleteError,        setDeleteError]        = useState('');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    dispatch(fetchUserProfile());
  }, []);

  useEffect(() => {
    if (user?.phone) setPhoneVal(user.phone);
  }, [user?.phone]);

  const savedAddresses = user?.savedAddresses || [];

  // ── Logout ────────────────────────────────────────────────
  const handleLogout = () => {
    dispatch(logout());
    dispatch(clearCart());
    navigate('/login');
  };

  // ── Phone save ────────────────────────────────────────────
  const handlePhoneSave = async () => {
    setPhoneError('');
    const cleaned = phoneVal.replace(/[\s\-\(\)]/g, '');
    if (!/^[\+]?[1-9][\d]{9,14}$/.test(cleaned)) {
      setPhoneError('Enter a valid 10-digit mobile number');
      return;
    }
    setPhoneSaving(true);
    await dispatch(updateUserProfile({ phone: cleaned }));
    setPhoneSaving(false);
    setEditingPhone(false);
    setPhoneSuccess(true);
    setTimeout(() => setPhoneSuccess(false), 3000);
  };

  // ── Address save ──────────────────────────────────────────
  const handleAddrSave = async (e) => {
    e.preventDefault();
    setAddrError('');
    if (!addrForm.address.trim() || !addrForm.city.trim() || !addrForm.state.trim() || !addrForm.postalCode.trim()) {
      setAddrError('Please fill all required fields');
      return;
    }
    setAddrSaving(true);
    const result = await dispatch(saveUserAddress(addrForm));
    setAddrSaving(false);
    if (saveUserAddress.rejected.match(result)) {
      setAddrError(result.payload?.message || 'Failed to save address');
    } else {
      setShowAddrForm(false);
      setAddrForm(EMPTY_ADDR);
    }
  };

  const handleAddrDelete = async (id) => {
    setDeletingId(id);
    await dispatch(deleteUserAddress(id));
    setDeletingId(null);
  };

  // ── Delete account ────────────────────────────────────────
  const openDeleteModal = () => {
    setDeleteConfirmText('');
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return; // don't allow close while in-flight
    setShowDeleteModal(false);
    setDeleteConfirmText('');
    setDeleteError('');
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Type DELETE (all caps) to confirm');
      return;
    }
    setDeleteLoading(true);
    const result = await dispatch(deleteAccount());
    setDeleteLoading(false);

    if (deleteAccount.rejected.match(result)) {
      setDeleteError(result.payload?.message || 'Something went wrong. Please try again.');
      return;
    }

    // deleteAccount.fulfilled already clears Redux + localStorage.
    // Also clear cart and redirect.
    dispatch(clearCart());
    navigate('/login');
  };

  if (!user) return null;

  // ─────────────────────────────────────────────────────────
  return (
    <>
      <style>{profileStyles}</style>

      {/* ══════════════════════════════════════════════════
          DELETE ACCOUNT MODAL
      ══════════════════════════════════════════════════ */}
      {showDeleteModal && (
        <div className="pf-modal-overlay" onClick={closeDeleteModal}>
          <div className="pf-modal" onClick={(e) => e.stopPropagation()}>

            {/* Close */}
            <button className="pf-modal-close" onClick={closeDeleteModal} disabled={deleteLoading}>✕</button>

            {/* Heading */}
            <span className="pf-modal-eyebrow">Danger Zone</span>
            <h2 className="pf-modal-title">Delete <span className="pf-modal-title-bold">Account</span></h2>
            <div className="pf-modal-rule"><hr className="pf-hr" /></div>

            {/* Warning block */}
            <div className="pf-modal-warning">
              <p className="pf-modal-warning-head">This action cannot be undone.</p>
              <ul className="pf-modal-warning-list">
                <li>Your login access will be revoked immediately.</li>
                <li>Saved addresses and contact details will be erased.</li>
                <li>Your account will be permanently wiped after 30 days.</li>
              </ul>
              <p className="pf-modal-warning-retain">
                Orders, invoices, and transaction records are retained for legal
                and accounting compliance and cannot be removed.
              </p>
            </div>

            {/* Confirm input */}
            <div className="pf-modal-confirm-block">
              <label className="pf-form-label" style={{ marginBottom: 10, display: 'block' }}>
                Type <span className="pf-modal-delete-word">DELETE</span> to confirm
              </label>
              <input
                className="pf-input pf-modal-confirm-input"
                type="text"
                value={deleteConfirmText}
                onChange={(e) => {
                  setDeleteConfirmText(e.target.value);
                  setDeleteError('');
                }}
                placeholder="DELETE"
                maxLength={10}
                disabled={deleteLoading}
                autoFocus
              />
            </div>

            {deleteError && <p className="pf-field-error" style={{ marginTop: 8 }}>{deleteError}</p>}

            {/* Actions */}
            <div className="pf-modal-actions">
              <button
                className="pf-btn-delete-confirm"
                onClick={handleDeleteAccount}
                disabled={deleteLoading || deleteConfirmText !== 'DELETE'}
              >
                {deleteLoading ? 'Deleting…' : 'Delete My Account'}
              </button>
              <button className="pf-btn-cancel" onClick={closeDeleteModal} disabled={deleteLoading}>
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      <div className="pf-root">

        {/* ── HEADER ── */}
        <div className="pf-header">
          <div>
            <span className="pf-eyebrow">My Account</span>
            <h1 className="pf-title">{user.name.split(' ')[0]}<span className="pf-title-bold">'s</span> Profile</h1>
          </div>
          <button className="pf-logout-btn" onClick={handleLogout}>Logout</button>
        </div>
        <div className="pf-rule-wrap"><hr className="pf-hr" /></div>

        {/* ── TABS ── */}
        <div className="pf-tabs">
          {[
            { key: 'orders',    label: 'My Orders' },
            { key: 'profile',   label: 'Profile'   },
            { key: 'addresses', label: 'Addresses' },
          ].map(t => (
            <button
              key={t.key}
              className={`pf-tab ${tab === t.key ? 'pf-tab--active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {t.key === 'addresses' && savedAddresses.length > 0 && (
                <span className="pf-tab-count">{savedAddresses.length}</span>
              )}
            </button>
          ))}
        </div>
        <div className="pf-rule-wrap" style={{ marginBottom: '40px' }}><hr className="pf-hr" /></div>

        {/* ══════════════════════════════════════════════════
            TAB: ORDERS
        ══════════════════════════════════════════════════ */}
        {tab === 'orders' && (
          <div className="pf-orders-wrap">
            <MyOrdersPage />
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            TAB: PROFILE
        ══════════════════════════════════════════════════ */}
        {tab === 'profile' && (
          <div className="pf-section">

            <div className="pf-panel-heading">
              <span className="pf-panel-eyebrow">Account Details</span>
              <h2 className="pf-panel-title">Your <span className="pf-panel-bold">Information</span></h2>
            </div>
            <div className="pf-panel-rule"><hr className="pf-hr" /></div>

            <div className="pf-info-grid">

              {/* Name — readonly */}
              <div className="pf-info-row">
                <span className="pf-info-label">Full Name</span>
                <span className="pf-info-value">{user.name}</span>
              </div>

              {/* Email — readonly with note */}
              <div className="pf-info-row">
                <span className="pf-info-label">Email</span>
                <div className="pf-info-right">
                  <span className="pf-info-value">{user.email}</span>
                  <span className="pf-info-note">Contact support to change email</span>
                </div>
              </div>

              {/* Phone — editable */}
              <div className="pf-info-row pf-info-row--phone">
                <span className="pf-info-label">Mobile</span>
                <div className="pf-info-right">
                  {editingPhone ? (
                    <div className="pf-phone-edit">
                      <input
                        type="tel"
                        value={phoneVal}
                        onChange={(e) => setPhoneVal(e.target.value)}
                        className="pf-input"
                        placeholder="10-digit mobile"
                        maxLength={15}
                        autoFocus
                      />
                      {phoneError && <span className="pf-field-error">{phoneError}</span>}
                      <div className="pf-phone-actions">
                        <button className="pf-btn-save" onClick={handlePhoneSave} disabled={phoneSaving}>
                          {phoneSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button className="pf-btn-cancel" onClick={() => { setEditingPhone(false); setPhoneError(''); setPhoneVal(user?.phone || ''); }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="pf-phone-display">
                      <span className="pf-info-value">{user.phone || '—'}</span>
                      <button
                        className="pf-edit-link"
                        onClick={() => { setEditingPhone(true); setPhoneVal(user?.phone || ''); }}
                      >
                        {user.phone ? 'Edit' : 'Add'}
                      </button>
                    </div>
                  )}
                  {phoneSuccess && <span className="pf-success">Mobile updated</span>}
                </div>
              </div>

            </div>

            {/* ── DANGER ZONE ──────────────────────────────────── */}
            <div className="pf-danger-zone">
              <div className="pf-danger-zone-header">
                <span className="pf-danger-eyebrow">Danger Zone</span>
                <h3 className="pf-danger-title">Delete <span className="pf-danger-title-bold">Account</span></h3>
              </div>
              <div className="pf-danger-rule"><hr className="pf-hr pf-hr--danger" /></div>
              <div className="pf-danger-body">
                <p className="pf-danger-desc">
                  Permanently deactivates your account and erases your personal data.
                  Orders and transaction records are retained for legal compliance.
                </p>
                <button className="pf-btn-danger" onClick={openDeleteModal}>
                  Delete Account
                </button>
              </div>
            </div>

          </div>
        )}

        {/* ══════════════════════════════════════════════════
            TAB: ADDRESSES
        ══════════════════════════════════════════════════ */}
        {tab === 'addresses' && (
          <div className="pf-section">

            <div className="pf-panel-heading">
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <span className="pf-panel-eyebrow">Saved Addresses</span>
                  <h2 className="pf-panel-title">Delivery <span className="pf-panel-bold">Addresses</span></h2>
                </div>
                {!showAddrForm && savedAddresses.length < 5 && (
                  <button className="pf-add-btn" onClick={() => { setShowAddrForm(true); setAddrError(''); setAddrForm(EMPTY_ADDR); }}>
                    + Add Address
                  </button>
                )}
              </div>
            </div>
            <div className="pf-panel-rule"><hr className="pf-hr" /></div>

            {/* Address cards */}
            {savedAddresses.length > 0 && (
              <div className="pf-addr-grid">
                {savedAddresses.map((addr) => (
                  <div key={addr._id} className={`pf-addr-card ${addr.isDefault ? 'pf-addr-card--default' : ''}`}>
                    <div className="pf-addr-card-top">
                      <span className="pf-addr-label">{addr.label || 'Home'}</span>
                      {addr.isDefault && <span className="pf-addr-default-badge">Default</span>}
                    </div>
                    {(addr.firstName || addr.lastName) && (
                      <p className="pf-addr-name">{[addr.firstName, addr.lastName].filter(Boolean).join(' ')}</p>
                    )}
                    <p className="pf-addr-line">{addr.address}</p>
                    <p className="pf-addr-line">{addr.city}, {addr.state} – {addr.postalCode}</p>
                    <p className="pf-addr-line">{addr.country}</p>
                    {addr.phone && <p className="pf-addr-phone">{addr.phone}</p>}
                    <button
                      className="pf-addr-delete"
                      onClick={() => handleAddrDelete(addr._id)}
                      disabled={deletingId === addr._id}
                    >
                      {deletingId === addr._id ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {savedAddresses.length === 0 && !showAddrForm && (
              <div className="pf-empty">
                <span className="pf-empty-label">No saved addresses yet</span>
                <button className="pf-add-btn" onClick={() => setShowAddrForm(true)}>+ Add your first address</button>
              </div>
            )}

            {savedAddresses.length >= 5 && (
              <p className="pf-limit-note">Maximum 5 addresses saved. Remove one to add another.</p>
            )}

            {/* Add address form */}
            {showAddrForm && (
              <form className="pf-addr-form" onSubmit={handleAddrSave}>
                <div className="pf-panel-rule" style={{ margin: savedAddresses.length ? '32px 0 28px' : '0 0 28px' }}><hr className="pf-hr" /></div>
                <span className="pf-panel-eyebrow" style={{ marginBottom: 20, display: 'block' }}>New Address</span>

                {/* Label */}
                <div className="pf-form-field">
                  <label className="pf-form-label">Label</label>
                  <div className="pf-label-options">
                    {['Home', 'Work', 'Other'].map(l => (
                      <button
                        key={l} type="button"
                        className={`pf-label-opt ${addrForm.label === l ? 'pf-label-opt--active' : ''}`}
                        onClick={() => setAddrForm({ ...addrForm, label: l })}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pf-form-row">
                  <div className="pf-form-field">
                    <label className="pf-form-label">First Name</label>
                    <input className="pf-input" value={addrForm.firstName}
                      onChange={e => setAddrForm({ ...addrForm, firstName: e.target.value })}
                      placeholder="First name" maxLength={50} />
                  </div>
                  <div className="pf-form-field">
                    <label className="pf-form-label">Last Name</label>
                    <input className="pf-input" value={addrForm.lastName}
                      onChange={e => setAddrForm({ ...addrForm, lastName: e.target.value })}
                      placeholder="Last name" maxLength={50} />
                  </div>
                </div>

                <div className="pf-form-field">
                  <label className="pf-form-label">Address <span className="pf-req">*</span></label>
                  <input className="pf-input" value={addrForm.address}
                    onChange={e => setAddrForm({ ...addrForm, address: e.target.value })}
                    placeholder="Street address, apartment, floor…" maxLength={200} required />
                </div>

                <div className="pf-form-row">
                  <div className="pf-form-field">
                    <label className="pf-form-label">City <span className="pf-req">*</span></label>
                    <input className="pf-input" value={addrForm.city}
                      onChange={e => setAddrForm({ ...addrForm, city: e.target.value })}
                      placeholder="City" maxLength={50} required />
                  </div>
                  <div className="pf-form-field">
                    <label className="pf-form-label">State <span className="pf-req">*</span></label>
                    <select className="pf-input pf-select" value={addrForm.state}
                      onChange={e => setAddrForm({ ...addrForm, state: e.target.value })} required>
                      <option value="">Select state</option>
                      {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div className="pf-form-row">
                  <div className="pf-form-field">
                    <label className="pf-form-label">PIN Code <span className="pf-req">*</span></label>
                    <input className="pf-input" value={addrForm.postalCode}
                      onChange={e => setAddrForm({ ...addrForm, postalCode: e.target.value })}
                      placeholder="6-digit PIN" maxLength={10} required />
                  </div>
                  <div className="pf-form-field">
                    <label className="pf-form-label">Phone</label>
                    <input className="pf-input" value={addrForm.phone}
                      onChange={e => setAddrForm({ ...addrForm, phone: e.target.value })}
                      placeholder="Mobile for delivery" maxLength={15} type="tel" />
                  </div>
                </div>

                {addrError && <span className="pf-field-error">{addrError}</span>}

                <div className="pf-form-actions">
                  <button type="submit" className="pf-btn-save pf-btn-save--full" disabled={addrSaving}>
                    {addrSaving ? 'Saving…' : 'Save Address'}
                  </button>
                  <button type="button" className="pf-btn-cancel" onClick={() => { setShowAddrForm(false); setAddrError(''); }}>
                    Cancel
                  </button>
                </div>
              </form>
            )}

          </div>
        )}

      </div>
    </>
  );
};

/* ── STYLES ── */
const profileStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

  .pf-root {
    background: #f8f8f8;
    min-height: 100vh;
    font-family: 'Exo 2', sans-serif;
    padding: 64px 80px 96px;
    max-width: 1400px;
    margin: 0 auto;
    box-sizing: border-box;
  }

  /* ── HEADER ── */
  .pf-header {
    display: flex; align-items: flex-end;
    justify-content: space-between; margin-bottom: 32px; gap: 16px;
  }
  .pf-eyebrow {
    display: flex; align-items: center; gap: 12px;
    font-size: 9px; font-weight: 400; letter-spacing: 0.5em;
    text-transform: uppercase; color: black; margin-bottom: 12px;
  }
  .pf-eyebrow::before {
    content: ''; width: 24px; height: 1px;
    background: rgba(0,0,0,0.2); display: block; flex-shrink: 0;
  }
  .pf-title {
    font-size: clamp(28px, 4vw, 52px); font-weight: 200; color: #000;
    letter-spacing: 0.08em; text-transform: uppercase; line-height: 1; margin: 0;
  }
  .pf-title-bold { font-weight: 600; }

  .pf-logout-btn {
    background: none; border: 1px solid rgba(0,0,0,0.2);
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 500; letter-spacing: 0.35em;
    text-transform: uppercase; color: black;
    padding: 10px 20px; cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
  }
  .pf-logout-btn:hover { border-color: #000; color: #000; }

  /* ── RULES ── */
  .pf-rule-wrap { margin-bottom: 0; }
  .pf-hr { border: none; border-top: 1px solid black; margin: 0; }
  .pf-hr--danger { border-top-color: rgba(160,0,0,0.15); }

  /* ── TABS ── */
  .pf-tabs {
    display: flex; gap: 0;
    margin-bottom: 0;
    margin-top: 32px;
  }
  .pf-tab {
    background: none; border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 8.5px; font-weight: 500; letter-spacing: 0.4em;
    text-transform: uppercase; color: rgba(0,0,0,0.35);
    padding: 12px 24px 12px 0; cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: color 0.2s, border-color 0.2s;
    display: flex; align-items: center; gap: 6px;
  }
  .pf-tab--active { color: #000; border-bottom-color: #000; }
  .pf-tab:hover:not(.pf-tab--active) { color: rgba(0,0,0,0.6); }
  .pf-tab-count {
    font-size: 7px; font-weight: 700; letter-spacing: 0.1em;
    background: #000; color: #fff;
    padding: 2px 5px; border-radius: 2px;
  }

  /* ── SECTION ── */
  .pf-section { max-width: 720px; }

  /* ── PANEL HEADINGS ── */
  .pf-panel-heading { margin-bottom: 20px; }
  .pf-panel-eyebrow {
    display: flex; align-items: center; gap: 10px;
    font-size: 8px; font-weight: 400; letter-spacing: 0.45em;
    text-transform: uppercase; color: black; margin-bottom: 10px;
  }
  .pf-panel-eyebrow::before {
    content: ''; width: 18px; height: 1px;
    background: rgba(0,0,0,0.15); display: block; flex-shrink: 0;
  }
  .pf-panel-title {
    font-size: clamp(20px, 2.5vw, 32px); font-weight: 200; color: #000;
    letter-spacing: 0.08em; text-transform: uppercase; line-height: 1; margin: 0;
  }
  .pf-panel-bold { font-weight: 600; }
  .pf-panel-rule { margin-bottom: 24px; }

  /* ── PROFILE INFO GRID ── */
  .pf-info-grid { display: flex; flex-direction: column; gap: 0; }
  .pf-info-row {
    display: flex; justify-content: space-between;
    align-items: center; gap: 24px;
    padding: 18px 0; border-bottom: 1px solid rgba(0,0,0,0.08);
  }
  .pf-info-row:last-child { border-bottom: none; }
  .pf-info-row--phone { align-items: flex-start; }
  .pf-info-label {
    font-size: 8px; font-weight: 500; letter-spacing: 0.45em;
    text-transform: uppercase; color: rgba(0,0,0,0.4); flex-shrink: 0; padding-top: 2px;
  }
  .pf-info-right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
  .pf-info-value { font-size: 13px; font-weight: 300; letter-spacing: 0.06em; color: #000; text-align: right; }
  .pf-info-note { font-size: 8px; font-weight: 300; letter-spacing: 0.2em; text-transform: uppercase; color: rgba(0,0,0,0.3); }

  /* ── PHONE EDIT ── */
  .pf-phone-display { display: flex; align-items: center; gap: 14px; }
  .pf-edit-link {
    font-size: 8px; font-weight: 500; letter-spacing: 0.3em;
    text-transform: uppercase; color: #000;
    background: none; border: none; cursor: pointer;
    padding-bottom: 1px; border-bottom: 1px solid rgba(0,0,0,0.2);
    transition: border-color 0.2s;
  }
  .pf-edit-link:hover { border-color: #000; }
  .pf-phone-edit { display: flex; flex-direction: column; gap: 8px; align-items: flex-end; }
  .pf-phone-actions { display: flex; gap: 8px; }

  /* ── INPUT ── */
  .pf-input {
    background: transparent; border: none; border-bottom: 1px solid black;
    outline: none; font-family: 'Exo 2', sans-serif;
    font-size: 13px; font-weight: 300; letter-spacing: 0.06em; color: #000;
    padding: 8px 0; transition: border-color 0.2s; border-radius: 0;
    -webkit-appearance: none; width: 100%;
  }
  .pf-input::placeholder { color: rgba(0,0,0,0.3); }
  .pf-input:focus { border-bottom-color: #000; }
  .pf-select { cursor: pointer; }

  /* ── BUTTONS ── */
  .pf-btn-save {
    background: #000; color: #fff; border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 600; letter-spacing: 0.35em;
    text-transform: uppercase; padding: 10px 20px; cursor: pointer;
    transition: background 0.2s;
  }
  .pf-btn-save:hover:not(:disabled) { background: #222; }
  .pf-btn-save:disabled { background: rgba(0,0,0,0.4); cursor: not-allowed; }
  .pf-btn-save--full { width: 100%; padding: 16px; font-size: 9px; }
  .pf-btn-cancel {
    background: none; border: 1px solid rgba(0,0,0,0.15);
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 500; letter-spacing: 0.3em;
    text-transform: uppercase; color: rgba(0,0,0,0.5);
    padding: 10px 16px; cursor: pointer; transition: border-color 0.2s;
  }
  .pf-btn-cancel:hover { border-color: rgba(0,0,0,0.4); }

  /* ── FEEDBACK ── */
  .pf-field-error { font-size: 9px; font-weight: 400; letter-spacing: 0.15em; color: #c00; text-transform: uppercase; }
  .pf-success { font-size: 9px; font-weight: 500; letter-spacing: 0.25em; text-transform: uppercase; color: #000; }

  /* ── ADDRESS GRID ── */
  .pf-addr-grid {
    display: grid; grid-template-columns: repeat(2, 1fr);
    gap: 12px; margin-bottom: 8px;
  }
  .pf-addr-card {
    padding: 20px; border: 1px solid rgba(0,0,0,0.1);
    position: relative; background: #fff; box-sizing: border-box;
  }
  .pf-addr-card--default { border-color: #000; }
  .pf-addr-card-top { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
  .pf-addr-label {
    font-size: 7px; font-weight: 600; letter-spacing: 0.3em; text-transform: uppercase;
    color: rgba(0,0,0,0.4); border: 1px solid rgba(0,0,0,0.1); padding: 2px 8px;
  }
  .pf-addr-default-badge {
    font-size: 7px; font-weight: 700; letter-spacing: 0.3em; text-transform: uppercase;
    color: #fff; background: #000; padding: 2px 8px;
  }
  .pf-addr-name { font-size: 11px; font-weight: 600; letter-spacing: 0.15em; text-transform: uppercase; color: #000; margin: 0 0 6px; }
  .pf-addr-line { font-size: 11px; font-weight: 300; letter-spacing: 0.04em; color: rgba(0,0,0,0.6); margin: 0 0 3px; line-height: 1.5; }
  .pf-addr-phone { font-size: 11px; font-weight: 300; letter-spacing: 0.04em; color: rgba(0,0,0,0.45); margin: 6px 0 0; }
  .pf-addr-delete {
    display: inline-block; margin-top: 14px;
    font-family: 'Exo 2', sans-serif;
    font-size: 7.5px; font-weight: 500; letter-spacing: 0.3em; text-transform: uppercase;
    color: rgba(0,0,0,0.3); background: none; border: none; cursor: pointer;
    padding: 0 0 1px; border-bottom: 1px solid rgba(0,0,0,0.1);
    transition: color 0.2s, border-color 0.2s;
  }
  .pf-addr-delete:hover { color: #c00; border-color: #c00; }
  .pf-addr-delete:disabled { opacity: 0.4; cursor: not-allowed; }

  /* ── ADD ADDRESS BUTTON ── */
  .pf-add-btn {
    background: none; border: 1px solid #000;
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 600; letter-spacing: 0.35em;
    text-transform: uppercase; color: #000;
    padding: 11px 22px; cursor: pointer;
    transition: background 0.2s, color 0.2s;
  }
  .pf-add-btn:hover { background: #000; color: #fff; }

  /* ── EMPTY STATE ── */
  .pf-empty {
    padding: 40px 0; display: flex; flex-direction: column;
    align-items: center; gap: 20px;
  }
  .pf-empty-label {
    font-size: 9px; font-weight: 400; letter-spacing: 0.4em;
    text-transform: uppercase; color: rgba(0,0,0,0.3);
  }

  .pf-limit-note {
    font-size: 9px; font-weight: 300; letter-spacing: 0.2em; text-transform: uppercase;
    color: rgba(0,0,0,0.35); margin-top: 12px;
  }

  /* ── ADDRESS FORM ── */
  .pf-addr-form { display: flex; flex-direction: column; gap: 20px; }
  .pf-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .pf-form-field { display: flex; flex-direction: column; gap: 8px; }
  .pf-form-label { font-size: 8px; font-weight: 500; letter-spacing: 0.45em; text-transform: uppercase; color: black; }
  .pf-req { color: black; }
  .pf-label-options { display: flex; gap: 6px; }
  .pf-label-opt {
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 500; letter-spacing: 0.25em; text-transform: uppercase;
    padding: 6px 14px; background: transparent;
    border: 1px solid rgba(0,0,0,0.15); cursor: pointer;
    color: rgba(0,0,0,0.4); transition: all 0.15s;
  }
  .pf-label-opt--active { border-color: #000; color: #000; background: #000; color: #fff; }
  .pf-form-actions { display: flex; flex-direction: column; gap: 8px; }

  /* ── ORDERS WRAP ── */
  .pf-orders-wrap { width: 100%; }

  /* ─────────────────────────────────────────────────────
     DANGER ZONE (inline, bottom of profile tab)
  ───────────────────────────────────────────────────── */
  .pf-danger-zone {
    margin-top: 56px;
    padding-top: 0;
  }
  .pf-danger-zone-header { margin-bottom: 16px; }
  .pf-danger-eyebrow {
    display: flex; align-items: center; gap: 10px;
    font-size: 8px; font-weight: 400; letter-spacing: 0.45em;
    text-transform: uppercase; color: rgba(160,0,0,0.6); margin-bottom: 10px;
  }
  .pf-danger-eyebrow::before {
    content: ''; width: 18px; height: 1px;
    background: rgba(160,0,0,0.2); display: block; flex-shrink: 0;
  }
  .pf-danger-title {
    font-size: clamp(18px, 2vw, 26px); font-weight: 200; color: #000;
    letter-spacing: 0.08em; text-transform: uppercase; line-height: 1; margin: 0;
  }
  .pf-danger-title-bold { font-weight: 600; }
  .pf-danger-rule { margin-bottom: 20px; }
  .pf-danger-body {
    display: flex; align-items: flex-start;
    justify-content: space-between; gap: 32px; flex-wrap: wrap;
  }
  .pf-danger-desc {
    font-size: 11px; font-weight: 300; letter-spacing: 0.04em;
    color: rgba(0,0,0,0.5); line-height: 1.7; margin: 0; max-width: 420px;
  }
  .pf-btn-danger {
    flex-shrink: 0;
    background: none; border: 1px solid rgba(160,0,0,0.35);
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 600; letter-spacing: 0.35em;
    text-transform: uppercase; color: rgba(160,0,0,0.7);
    padding: 11px 22px; cursor: pointer;
    transition: background 0.2s, color 0.2s, border-color 0.2s;
  }
  .pf-btn-danger:hover {
    background: rgba(160,0,0,0.06);
    border-color: rgba(160,0,0,0.6);
    color: #a00;
  }

  /* ─────────────────────────────────────────────────────
     DELETE ACCOUNT MODAL
  ───────────────────────────────────────────────────── */
  .pf-modal-overlay {
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(0,0,0,0.55);
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    backdrop-filter: blur(2px);
  }
  .pf-modal {
    background: #f8f8f8; max-width: 480px; width: 100%;
    padding: 40px 40px 36px; position: relative;
    font-family: 'Exo 2', sans-serif;
    box-shadow: 0 8px 48px rgba(0,0,0,0.18);
    animation: pf-modal-in 0.18s ease;
  }
  @keyframes pf-modal-in {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .pf-modal-close {
    position: absolute; top: 16px; right: 20px;
    background: none; border: none; cursor: pointer;
    font-family: 'Exo 2', sans-serif;
    font-size: 11px; color: rgba(0,0,0,0.35);
    line-height: 1; padding: 4px;
    transition: color 0.15s;
  }
  .pf-modal-close:hover:not(:disabled) { color: #000; }
  .pf-modal-close:disabled { opacity: 0.3; cursor: not-allowed; }

  .pf-modal-eyebrow {
    display: flex; align-items: center; gap: 10px;
    font-size: 8px; font-weight: 400; letter-spacing: 0.45em;
    text-transform: uppercase; color: rgba(160,0,0,0.6); margin-bottom: 10px;
  }
  .pf-modal-eyebrow::before {
    content: ''; width: 18px; height: 1px;
    background: rgba(160,0,0,0.2); display: block; flex-shrink: 0;
  }
  .pf-modal-title {
    font-size: clamp(22px, 3vw, 30px); font-weight: 200; color: #000;
    letter-spacing: 0.08em; text-transform: uppercase; line-height: 1; margin: 0 0 20px;
  }
  .pf-modal-title-bold { font-weight: 600; }
  .pf-modal-rule { margin-bottom: 24px; }

  /* Warning block */
  .pf-modal-warning {
    background: rgba(160,0,0,0.04);
    border-left: 2px solid rgba(160,0,0,0.25);
    padding: 16px 18px; margin-bottom: 28px;
  }
  .pf-modal-warning-head {
    font-size: 9px; font-weight: 600; letter-spacing: 0.3em;
    text-transform: uppercase; color: rgba(160,0,0,0.75);
    margin: 0 0 12px;
  }
  .pf-modal-warning-list {
    margin: 0 0 12px; padding-left: 14px;
    display: flex; flex-direction: column; gap: 6px;
  }
  .pf-modal-warning-list li {
    font-size: 10px; font-weight: 300; letter-spacing: 0.05em;
    color: rgba(0,0,0,0.65); line-height: 1.5;
  }
  .pf-modal-warning-retain {
    font-size: 9px; font-weight: 300; letter-spacing: 0.05em;
    color: rgba(0,0,0,0.4); line-height: 1.6;
    margin: 0; border-top: 1px solid rgba(0,0,0,0.07); padding-top: 10px;
  }

  /* Confirm input */
  .pf-modal-confirm-block { margin-bottom: 4px; }
  .pf-modal-delete-word {
    font-weight: 700; letter-spacing: 0.25em; color: rgba(160,0,0,0.7);
  }
  .pf-modal-confirm-input {
    letter-spacing: 0.25em; font-weight: 500;
  }

  /* Modal actions */
  .pf-modal-actions {
    display: flex; flex-direction: column; gap: 10px; margin-top: 24px;
  }
  .pf-btn-delete-confirm {
    background: #000; color: #fff; border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 600; letter-spacing: 0.35em;
    text-transform: uppercase; padding: 16px; cursor: pointer;
    transition: background 0.2s, opacity 0.2s;
    width: 100%;
  }
  .pf-btn-delete-confirm:disabled {
    background: rgba(0,0,0,0.25);
    cursor: not-allowed;
  }
  .pf-btn-delete-confirm:hover:not(:disabled) { background: #1a0000; }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .pf-root { padding: 48px 40px 80px; }
  }
  @media (max-width: 768px) {
    .pf-root { padding: 32px 20px 64px; }
    .pf-header { flex-direction: column; align-items: flex-start; gap: 16px; }
    .pf-addr-grid { grid-template-columns: 1fr; }
    .pf-form-row { grid-template-columns: 1fr; }
    .pf-tabs { overflow-x: auto; padding-bottom: 4px; }
    .pf-tab { white-space: nowrap; }
    .pf-section { max-width: 100%; }
    .pf-danger-body { flex-direction: column; gap: 16px; }
    .pf-btn-danger { width: 100%; text-align: center; }
    .pf-modal { padding: 32px 24px 28px; }
  }
  @media (max-width: 360px) {
    .pf-root { padding: 24px 14px 48px; }
  }
`;

export default Profile;
